import { Select } from "./select";
import { TableScroll } from "./table-scroll";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Doodle, PageHeader, Sticker, titleClass } from "./design-system";
import { useQueryClient } from "@tanstack/react-query";
import {
  CreditCard,
  Download,
  FileText,
  MapPin,
  Pencil,
  Camera,
  Save,
  UserRound,
} from "lucide-react";
import { apiFetch, apiUrl, AUTH_TOKEN_KEY, authClient } from "./lib/auth";
import { LoadingBlock } from "./loading";
import { ConfirmDialog, type ConfirmRequest } from "./confirm-dialog";
import { cn } from "@hey404/ui";
import {
  btnPrimary,
  btnSecondary,
  btnDestructive,
  fieldErrorClass,
  inputClass,
  labelClass,
} from "./admin-ui";
import { Avatar } from "./avatar";
import { AvatarCropModal } from "./avatar-crop";
import { useToast } from "./toast";

type AccountData = {
  profile: {
    country: string;
    city: string;
    timezone: string;
    phone: string;
    headline: string;
    bio: string;
    avatarUrl: string | null;
    public: boolean;
    // null = not set → defaults to the total of the student's courses (UNB2-165).
    weeklyGoalMinutes: number | null;
  } | null;
  billing: {
    legalName: string;
    taxId: string;
    taxCondition: string;
    address: string;
    postalCode: string;
    country: string;
  } | null;
  subscription: {
    interval: "monthly" | "annual";
    status: "pending" | "active" | "past_due" | "canceled";
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  invoices: Array<{
    id: string;
    number: string;
    description: string;
    amountMinor: number;
    currency: string;
    status: string;
    issuedAt: string;
    documentUrl: string | null;
  }>;
  // Cap for the weekly goal input = total minutes of the student's courses (UNB2-165).
  maxWeeklyMinutes: number;
};
// The shared input, roomier for these long forms.
// font-sans: the fields sit inside mono labels and would inherit them.
const field = cn(inputClass, "mt-1.5 font-sans");
const label = labelClass;

// Country dropdown options: Spanish names derived from ISO 3166-1 codes via Intl
// (so we don't hand-maintain ~200 translations), deduped and sorted. Used for the
// residence and tax-country selects instead of free-text inputs (UNB2-172).
const REGION_NAMES = new Intl.DisplayNames(["es"], { type: "region" });
const COUNTRIES =
  "AD AE AF AG AL AM AO AR AT AU AZ BA BB BD BE BF BG BH BI BJ BN BO BR BS BT BW BY BZ CA CD CF CG CH CI CL CM CN CO CR CU CV CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FM FR GA GB GD GE GH GM GN GQ GR GT GW GY HN HR HT HU ID IE IL IN IQ IR IS IT JM JO JP KE KG KH KI KM KN KP KR KW KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MG MH MK ML MM MN MR MT MU MV MW MX MY MZ NA NE NG NI NL NO NP NR NZ OM PA PE PG PH PK PL PT PW PY QA RO RS RU RW SA SB SC SD SE SG SI SK SL SM SN SO SR SS ST SV SY SZ TD TG TH TJ TL TM TN TO TR TT TV TZ UA UG US UY UZ VA VC VE VN VU WS YE ZA ZM ZW"
    .split(" ")
    .map((code) => {
      try {
        return REGION_NAMES.of(code) ?? code;
      } catch {
        return code;
      }
    })
    .filter((name) => name.length > 2)
    .sort((a, b) => a.localeCompare(b, "es"));

// Full IANA time-zone list for the zone dropdown; fall back to a small LATAM-first
// set on the rare engine without Intl.supportedValuesOf.
const TIMEZONES: string[] = (
  Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
).supportedValuesOf?.("timeZone") ?? [
  "America/Argentina/Buenos_Aires",
  "America/Bogota",
  "America/Mexico_City",
  "America/Santiago",
  "America/Lima",
  "America/Montevideo",
  "America/Sao_Paulo",
  "America/New_York",
  "Europe/Madrid",
  "UTC",
];

// A country <Select> that keeps any pre-existing free-text value selectable even
// if it isn't one of the Intl-derived names (so an old profile never loses it).
function CountrySelect({
  name,
  defaultValue,
}: {
  name: string;
  defaultValue?: string | null;
}) {
  const current = defaultValue ?? "";
  const options =
    current && !COUNTRIES.includes(current)
      ? [current, ...COUNTRIES]
      : COUNTRIES;
  return (
    <Select
      name={name}
      defaultValue={current}
      className={`${field} cursor-pointer`}
    >
      <option value="">Selecciona un país…</option>
      {options.map((country) => (
        <option key={country} value={country}>
          {country}
        </option>
      ))}
    </Select>
  );
}

// A time-zone <Select>; like CountrySelect it preserves an out-of-list stored value.
function TimezoneSelect({ defaultValue }: { defaultValue: string }) {
  const options = TIMEZONES.includes(defaultValue)
    ? TIMEZONES
    : [defaultValue, ...TIMEZONES];
  return (
    <Select
      name="timezone"
      defaultValue={defaultValue}
      className={`${field} cursor-pointer`}
    >
      {options.map((tz) => (
        <option key={tz} value={tz}>
          {tz}
        </option>
      ))}
    </Select>
  );
}
const money = (amount: number, currency: string) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency }).format(
    amount / 100,
  );

// The receipt PDF (UNB2-174) is an authenticated endpoint, so fetch it with the
// bearer token and download the blob rather than linking to it directly.
async function downloadInvoicePdf(
  invoice: { id: string; number: string },
  onError: () => void,
) {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const res = await fetch(`${apiUrl}/api/account/invoices/${invoice.id}/pdf`, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) return onError();
  const url = URL.createObjectURL(await res.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${invoice.number}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function AccountSettings({
  user,
}: {
  user: { name: string; email: string };
}) {
  const [data, setData] = useState<AccountData | null>(null);
  const [error, setError] = useState("");
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirmReq, setConfirmReq] = useState<ConfirmRequest | null>(null);
  const load = () => apiFetch<AccountData>("/api/account").then(setData);
  useEffect(() => {
    load().catch(() => setError("No pudimos cargar los datos de tu cuenta."));
  }, []);
  const save = async (action: () => Promise<unknown>, message: string) => {
    try {
      await action();
      await load();
      toast(message);
      // Refresh the sidebar (avatar/counts) live — no page reload needed.
      void queryClient.invalidateQueries({
        queryKey: ["student", "navigation"],
      });
    } catch {
      toast("No pudimos guardar los cambios. Intenta nuevamente.", "error");
    }
  };
  const values = (event: FormEvent<HTMLFormElement>) =>
    Object.fromEntries(new FormData(event.currentTarget));
  if (!data && error)
    return (
      <div
        role="alert"
        className="border-ink border-l-danger bg-paper border-surface border-l-8 p-6"
      >
        <strong className="font-display text-xl">
          No pudimos cargar tu cuenta.
        </strong>
        <p className="text-ink-soft mt-2 text-sm">
          Revisa tu conexión y vuelve a intentarlo.
        </p>
        <button
          type="button"
          onClick={() => {
            setError("");
            load().catch(() =>
              setError("No pudimos cargar los datos de tu cuenta."),
            );
          }}
          className={`${btnSecondary} mt-4 px-4 py-2`}
        >
          Reintentar
        </button>
      </div>
    );
  if (!data) return <SettingsLoading />;
  const subscription = data.subscription;
  // The date access runs out after a cancellation — same as the next renewal
  // date while active. Long form ("13 de septiembre de 2026") so it reads clearly
  // wherever we tell the student what "hasta el final del período" actually means.
  const cutoffDate = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString("es-AR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;
  // Re-subscribe after a cancellation: the current interval just turns renewal
  // back on (no charge); the other interval is a plan switch, so we preview the
  // prorated charge first — same as the "Pasar a anual" flow.
  const resubscribe = async (interval: "monthly" | "annual") => {
    if (!subscription) return;
    const label = interval === "annual" ? "anual" : "mensual";
    if (interval === subscription.interval) {
      setConfirmReq({
        title: `Reactivar plan ${label}`,
        message: "Se reactiva la renovación automática de tu plan.",
        detail: cutoffDate
          ? `Vuelve a renovarse el ${cutoffDate}. No se te cobra nada ahora.`
          : "No se te cobra nada ahora.",
        confirmLabel: "Reactivar",
        onConfirm: () =>
          save(
            () =>
              apiFetch("/api/account/subscription/resume", { method: "POST" }),
            "Renovación reactivada.",
          ),
      });
      return;
    }
    const preview = await apiFetch<{
      amountMinor: number | null;
      currency?: string;
    }>(`/api/account/subscription/change-preview?interval=${interval}`).catch(
      () => null,
    );
    const amount =
      preview && preview.amountMinor != null
        ? new Intl.NumberFormat("es", {
            style: "currency",
            currency: preview.currency ?? "USD",
          }).format(preview.amountMinor / 100)
        : null;
    setConfirmReq({
      title: `Volver a suscribirte — plan ${label}`,
      message: `Reactivas la suscripción y pasas al plan ${label} ahora. Stripe ajusta el cobro por lo que resta del período.`,
      detail: amount
        ? `Se te cobrará ${amount} ahora (prorrateado).`
        : "Se aplicará un cobro prorrateado ahora.",
      confirmLabel: "Confirmar y pagar",
      onConfirm: () =>
        save(
          () =>
            apiFetch("/api/account/subscription/change", {
              method: "POST",
              body: JSON.stringify({ interval }),
            }),
          `Volviste a suscribirte al plan ${label}.`,
        ),
    });
  };
  return (
    <>
      <ConfirmDialog request={confirmReq} onClose={() => setConfirmReq(null)} />
      <SettingsTop />
      {/* Two columns only while the billing card sits next to the profile. */}
      <div
        className={`mt-8 grid items-start gap-7 ${SHOW_BILLING ? "xl:grid-cols-2" : ""}`}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const value = values(event);
            save(async () => {
              const result = await authClient.updateUser({
                name: String(value.name).trim(),
              });
              if (result.error) throw new Error("profile_failed");
              await apiFetch("/api/account/profile", {
                method: "PUT",
                body: JSON.stringify({
                  country: value.country,
                  city: value.city,
                  timezone: value.timezone,
                  phone: value.phone,
                  headline: value.headline,
                  bio: value.bio,
                  avatarUrl: value.avatarUrl || null,
                  public: value.public === "on",
                  weeklyGoalMinutes: Number(value.weeklyGoalMinutes),
                }),
              });
            }, "Perfil guardado.");
          }}
          id="perfil"
          className="border-ink bg-bone shadow-brutal border-surface scroll-mt-24 p-6"
        >
          <SettingsHeading
            icon={UserRound}
            tone="bg-acid"
            kicker="Perfil"
            title="Datos personales"
            description="Se usan en tu perfil y certificados."
          />
          {/* Compact layout: the photo beside who you are (name, headline,
              bio), the contact details three to a row, and the public toggle
              sharing the footer with the save button. */}
          <div className="mt-5 grid gap-5 md:grid-cols-[9rem_minmax(0,1fr)]">
            <AvatarField
              defaultUrl={data.profile?.avatarUrl ?? ""}
              userName={user.name}
            />
            <div className="grid content-start gap-4 sm:grid-cols-2">
              <label className={label}>
                Nombre completo
                <input
                  name="name"
                  defaultValue={user.name}
                  required
                  minLength={2}
                  className={field}
                />
              </label>
              <label className={label}>
                Perfil profesional
                <input
                  name="headline"
                  defaultValue={data.profile?.headline}
                  placeholder="Diseñadora de producto · No-code"
                  className={field}
                />
              </label>
              <label className={`${label} sm:col-span-2`}>
                Biografía
                <textarea
                  name="bio"
                  rows={2}
                  defaultValue={data.profile?.bio}
                  placeholder="Cuéntale a la comunidad qué haces y qué quieres construir."
                  className={cn(field, "resize-y")}
                />
              </label>
            </div>
          </div>
          <div className="border-ink/20 mt-5 grid gap-4 border-t border-dashed pt-5 sm:grid-cols-2 lg:grid-cols-3">
            <label className={label}>
              Email
              <input
                value={user.email}
                readOnly
                aria-readonly="true"
                title="Tu email de acceso no se cambia desde aquí."
                className={cn(field, "bg-ink/5 text-ink-soft")}
              />
            </label>
            <label className={label}>
              País de residencia
              <CountrySelect
                name="country"
                defaultValue={data.profile?.country}
              />
            </label>
            <label className={label}>
              Ciudad
              <input
                name="city"
                autoComplete="address-level2"
                defaultValue={data.profile?.city}
                className={field}
              />
            </label>
            <label className={label}>
              Teléfono
              <input
                name="phone"
                type="tel"
                autoComplete="tel"
                defaultValue={data.profile?.phone}
                className={field}
              />
            </label>
            <label className={label}>
              Zona horaria
              <TimezoneSelect
                defaultValue={
                  data.profile?.timezone ||
                  Intl.DateTimeFormat().resolvedOptions().timeZone
                }
              />
            </label>
            <label className={label}>
              <span className="flex items-baseline justify-between gap-2">
                Objetivo semanal
                <span className="text-ink-soft font-mono text-xs font-normal">
                  máx {data.maxWeeklyMinutes} min
                </span>
              </span>
              <span className="relative block">
                <input
                  name="weeklyGoalMinutes"
                  type="number"
                  min="15"
                  max={data.maxWeeklyMinutes}
                  defaultValue={
                    data.profile?.weeklyGoalMinutes ?? data.maxWeeklyMinutes
                  }
                  className={cn(field, "pr-14")}
                />
                <span className="text-ink-soft pointer-events-none absolute bottom-0 right-3 top-1.5 grid place-items-center font-mono text-xs">
                  min
                </span>
              </span>
            </label>
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
            <label className="flex cursor-pointer items-center gap-3">
              <input
                name="public"
                type="checkbox"
                defaultChecked={data.profile?.public}
                className="accent-ink size-5 shrink-0"
              />
              <span>
                <span className="font-mono text-sm font-bold">
                  Perfil público
                </span>
                <span className="text-ink-soft block text-xs">
                  Otras personas del curso ven tu nombre, foto y perfil.
                </span>
              </span>
            </label>
            <button className={`${btnPrimary} gap-2 px-4 py-3 text-sm`}>
              <Save size={16} /> Guardar perfil
            </button>
          </div>
        </form>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const value = values(event);
            save(
              () =>
                apiFetch("/api/account/billing", {
                  method: "PUT",
                  body: JSON.stringify(value),
                }),
              "Datos de facturación guardados.",
            );
          }}
          id="facturacion"
          hidden={!SHOW_BILLING}
          className="border-ink bg-bone shadow-brutal border-surface scroll-mt-24 p-6"
        >
          <SettingsHeading
            icon={MapPin}
            tone="bg-lemon"
            kicker="Facturación"
            title="Datos de facturación"
            description="Los usamos para emitir tus comprobantes."
          />
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <label className={`${label} sm:col-span-2`}>
              Nombre o razón social
              <input
                name="legalName"
                defaultValue={data.billing?.legalName}
                className={field}
              />
            </label>
            <label className={label}>
              CUIT / identificación fiscal
              <input
                name="taxId"
                defaultValue={data.billing?.taxId}
                className={field}
              />
            </label>
            <label className={label}>
              Condición fiscal
              <Select
                name="taxCondition"
                defaultValue={data.billing?.taxCondition || "consumer"}
                className={field}
              >
                <option value="consumer">Consumidor final</option>
                <option value="monotributista">Monotributista</option>
                <option value="responsable_inscripto">
                  Responsable inscripto
                </option>
                <option value="exempt">Exento</option>
                <option value="foreign">Persona del exterior</option>
              </Select>
            </label>
            <label className={`${label} sm:col-span-2`}>
              Domicilio fiscal
              <input
                name="address"
                autoComplete="street-address"
                defaultValue={data.billing?.address}
                className={field}
              />
            </label>
            <label className={label}>
              Código postal
              <input
                name="postalCode"
                autoComplete="postal-code"
                defaultValue={data.billing?.postalCode}
                className={field}
              />
            </label>
            <label className={label}>
              País fiscal
              <CountrySelect
                name="country"
                defaultValue={data.billing?.country}
              />
            </label>
          </div>
          <button className={`${btnPrimary} mt-6 gap-2 px-4 py-3 text-sm`}>
            <Save size={16} /> Guardar facturación
          </button>
        </form>
      </div>
      <section
        id="suscripcion"
        hidden={!SHOW_SUBSCRIPTION}
        className="border-ink bg-grape shadow-brutal-lg border-surface relative mt-7 scroll-mt-24 p-6"
      >
        <Doodle
          name="flower-yellow"
          float
          tilt={-8}
          className="absolute -right-5 -top-7 hidden w-20 sm:block"
        />
        <SettingsHeading
          icon={CreditCard}
          tone="bg-paper"
          kicker="Plan"
          title="Suscripción"
          description="Acceso a las cohortes y contenidos de hey404."
        />
        {subscription && subscription.status !== "canceled" ? (
          <div className="border-ink bg-paper border-surface shadow-brutal mt-6 flex flex-wrap items-center justify-between gap-5 p-5">
            <div>
              <span className="font-mono text-xs font-bold uppercase">
                Plan {subscription.interval === "annual" ? "anual" : "mensual"}
              </span>
              <h3 className="font-display mt-1 text-2xl font-extrabold">
                {subscription.status === "active"
                  ? "Suscripción activa"
                  : subscription.status === "pending"
                    ? "Activación pendiente"
                    : "Pago pendiente"}
              </h3>
              {subscription.cancelAtPeriodEnd ? (
                <div className="border-ink bg-lemon border-chip mt-2 p-3">
                  <p className="font-display text-sm font-extrabold">
                    Renovación cancelada
                  </p>
                  <p className="mt-1 text-sm">
                    No se hará ningún cobro nuevo.{" "}
                    {cutoffDate
                      ? `Mantienes el acceso hasta el ${cutoffDate}; después de esa fecha la suscripción queda cancelada.`
                      : "Mantienes el acceso hasta el final del período actual."}
                  </p>
                </div>
              ) : (
                <p className="text-ink-soft mt-1 text-sm">
                  {subscription.currentPeriodEnd
                    ? `Próxima renovación: ${cutoffDate}`
                    : "Te enviaremos las instrucciones para completar la activación."}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              {subscription.cancelAtPeriodEnd && (
                <>
                  <button
                    onClick={() => void resubscribe("monthly")}
                    className={`${subscription.interval === "monthly" ? btnPrimary : btnSecondary} px-4 py-3 text-sm`}
                  >
                    Reactivar mensual
                  </button>
                  <button
                    onClick={() => void resubscribe("annual")}
                    className={`${subscription.interval === "annual" ? btnPrimary : btnSecondary} px-4 py-3 text-sm`}
                  >
                    Reactivar anual
                  </button>
                </>
              )}
              {subscription.status === "active" &&
                subscription.interval === "monthly" &&
                !subscription.cancelAtPeriodEnd && (
                  <button
                    onClick={async () => {
                      const preview = await apiFetch<{
                        amountMinor: number | null;
                        currency?: string;
                      }>(
                        "/api/account/subscription/change-preview?interval=annual",
                      ).catch(() => null);
                      const amount =
                        preview && preview.amountMinor != null
                          ? new Intl.NumberFormat("es", {
                              style: "currency",
                              currency: preview.currency ?? "USD",
                            }).format(preview.amountMinor / 100)
                          : null;
                      setConfirmReq({
                        title: "Pasar al plan anual",
                        message:
                          "Tu plan cambiará a anual ahora mismo. Stripe ajusta el cobro por lo que resta del mes.",
                        detail: amount
                          ? `Se te cobrará ${amount} ahora (prorrateado).`
                          : "Se aplicará un cobro prorrateado ahora.",
                        confirmLabel: "Confirmar y pagar",
                        onConfirm: () =>
                          save(
                            () =>
                              apiFetch("/api/account/subscription/change", {
                                method: "POST",
                                body: JSON.stringify({ interval: "annual" }),
                              }),
                            "Cambiaste al plan anual.",
                          ),
                      });
                    }}
                    className={`${btnPrimary} px-4 py-3 text-sm`}
                  >
                    Pasar a anual
                  </button>
                )}
              {!subscription.cancelAtPeriodEnd && (
                <button
                  onClick={() =>
                    setConfirmReq({
                      title: "Cancelar la renovación",
                      message:
                        "Cancelas la renovación automática — no pierdes el acceso ahora.",
                      detail: cutoffDate
                        ? `Mantienes el acceso hasta el ${cutoffDate} y no se te cobrará de nuevo. Después de esa fecha, la suscripción queda cancelada.`
                        : "Mantienes el acceso hasta el final del período actual y no se te cobrará de nuevo.",
                      confirmLabel: "Sí, cancelar la renovación",
                      destructive: true,
                      onConfirm: () =>
                        save(
                          () =>
                            apiFetch("/api/account/subscription/cancel", {
                              method: "POST",
                            }),
                          cutoffDate
                            ? `Listo. Cancelaste la renovación — tienes acceso hasta el ${cutoffDate}.`
                            : "Listo. Cancelaste la renovación; mantienes el acceso hasta el final del período.",
                        ),
                    })
                  }
                  className={`${btnDestructive} px-4 py-3 text-sm`}
                >
                  Cancelar suscripción
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <Plan
              title="Mensual"
              detail="Flexibilidad mes a mes."
              onSelect={() =>
                save(async () => {
                  const result = await apiFetch<{ redirectUrl: string | null }>(
                    "/api/account/subscription",
                    {
                      method: "POST",
                      body: JSON.stringify({ interval: "monthly" }),
                    },
                  );
                  if (result.redirectUrl)
                    window.location.assign(result.redirectUrl);
                }, "Checkout mensual creado.")
              }
            />
            <Plan
              title="Anual"
              detail="Acceso a todas las cohortes del año."
              highlighted
              onSelect={() =>
                save(async () => {
                  const result = await apiFetch<{ redirectUrl: string | null }>(
                    "/api/account/subscription",
                    {
                      method: "POST",
                      body: JSON.stringify({ interval: "annual" }),
                    },
                  );
                  if (result.redirectUrl)
                    window.location.assign(result.redirectUrl);
                }, "Checkout anual creado.")
              }
            />
          </div>
        )}
      </section>
      <section
        id="comprobantes"
        className="border-ink bg-bone shadow-brutal border-surface mt-7 scroll-mt-24 p-6"
      >
        <SettingsHeading
          icon={FileText}
          tone="bg-bubble"
          kicker="Historial"
          title="Facturas y comprobantes"
          description="Historial de compras de cursos y suscripciones."
        />
        {data.invoices.length ? (
          <TableScroll
            count={data.invoices.length}
            noun="comprobantes"
            className="mt-6"
          >
            <table className="data-table min-w-[620px]">
              <thead>
                <tr>
                  <th>Factura</th>
                  <th>Fecha</th>
                  <th>Concepto</th>
                  <th className="num">Total</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="code">{invoice.number}</td>
                    <td>
                      {new Date(invoice.issuedAt).toLocaleDateString("es-AR")}
                    </td>
                    <td>{invoice.description}</td>
                    <td className="num font-bold">
                      {money(invoice.amountMinor, invoice.currency)}
                    </td>
                    <td>
                      <InvoiceStatus status={invoice.status} />
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() =>
                          downloadInvoicePdf(invoice, () =>
                            toast(
                              "No pudimos generar el comprobante.",
                              "error",
                            ),
                          )
                        }
                        aria-label={`Descargar ${invoice.number}`}
                        title="Descargar PDF"
                        className={cn(btnSecondary, "h-10 w-10 px-0")}
                      >
                        <Download size={16} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        ) : (
          <div className="border-ink/30 border-chip mt-6 border-dashed p-8 text-center">
            <Doodle name="exclamation-pink" tilt={8} className="mx-auto w-9" />
            <p className="font-display mt-3 text-xl font-extrabold">
              Todavía no hay facturas.
            </p>
            <p className="text-ink-soft mt-1 text-sm">
              Cuando se confirme una compra o suscripción aparecerá aquí.
            </p>
          </div>
        )}
      </section>
    </>
  );
}

// Avatar URL with a live preview, so pasting a link shows the photo right away
// (and confirms it's the same Avatar the rest of the app renders).
// Preview-only avatar picker. Selecting (or clicking the preview) opens the crop
// modal; "Aplicar" uploads just the cropped square and shows it here. Nothing is
// committed to the profile until the user saves the form — the sidebar avatar
// (the query invalidated in `save`) updates only then.
function AvatarField({
  defaultUrl,
  userName,
}: {
  defaultUrl: string;
  userName: string;
}) {
  const [url, setUrl] = useState(defaultUrl);
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadBlob = async (blob: Blob) => {
    setUploadError("");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", new File([blob], "avatar.jpg", { type: blob.type }));
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const res = await fetch(`${apiUrl}/api/account/avatar`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!res.ok) throw new Error();
      const data = (await res.json()) as { avatarUrl: string };
      setUrl(data.avatarUrl);
      setCropOpen(false);
    } catch {
      setUploadError("No pudimos subir la imagen. Prueba otra (máx 2 MB).");
    } finally {
      setUploading(false);
    }
  };
  // Click the preview: re-crop the last photo if we have it, else pick a new one.
  const onPreview = () =>
    sourceFile ? setCropOpen(true) : inputRef.current?.click();
  return (
    <div className="flex items-center gap-4 md:flex-col md:items-start md:gap-2">
      <span className={cn(label, "hidden md:block")}>Foto de perfil</span>
      {/* The photo is the control: click it to pick or re-crop; a small
          camera badge on its corner says so without a full-width button. */}
      <div className="relative shrink-0">
        <button
          type="button"
          onClick={onPreview}
          aria-label={
            url
              ? "Cambiar o reencuadrar la foto de perfil"
              : "Subir foto de perfil"
          }
          className="group relative block cursor-pointer"
        >
          <Avatar
            name={userName}
            url={url}
            className="border-ink bg-lemon h-24 w-24 text-2xl"
          />
          <span className="bg-ink/45 text-paper absolute inset-0 grid place-items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
            <Pencil size={18} aria-hidden="true" />
          </span>
        </button>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          aria-label={url ? "Subir otra foto" : "Subir foto"}
          title={url ? "Subir otra foto" : "Subir foto"}
          className="border-ink bg-paper border-chip shadow-brutal hover:bg-acid absolute -bottom-2 -right-2 grid h-9 w-9 cursor-pointer place-items-center rounded-full transition-colors disabled:cursor-wait"
        >
          {uploading ? (
            <span className="font-mono text-[10px] font-bold">…</span>
          ) : (
            <Camera size={16} aria-hidden="true" />
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) {
              setUploadError("");
              setSourceFile(file);
              setCropOpen(true);
            }
          }}
        />
      </div>
      <p className="text-ink-soft text-xs leading-snug md:mt-2 md:max-w-36">
        <span className="text-ink font-mono font-bold md:hidden">
          Foto de perfil
          <br />
        </span>
        {uploading
          ? "Subiendo…"
          : "JPG o PNG, máx. 2 MB. Se aplica al guardar."}
      </p>
      {/* The photo travels with the form; the URL field is gone (uploading
          is the way in), so it rides along hidden. */}
      <input type="hidden" name="avatarUrl" value={url} />
      {uploadError && (
        <span className={cn(fieldErrorClass, "mt-2 block normal-case")}>
          {uploadError}
        </span>
      )}
      {cropOpen && sourceFile && (
        <AvatarCropModal
          file={sourceFile}
          busy={uploading}
          onCancel={() => setCropOpen(false)}
          onApply={uploadBlob}
        />
      )}
    </div>
  );
}

function Plan({
  title,
  detail,
  highlighted,
  onSelect,
}: {
  title: string;
  detail: string;
  highlighted?: boolean;
  onSelect: () => void;
}) {
  return (
    <article
      className={`border-ink border-surface relative p-5 ${highlighted ? "bg-acid shadow-brutal-lg" : "bg-paper shadow-brutal"}`}
    >
      {highlighted && (
        <Sticker tone="lemon" tilt={4} className="absolute -top-3 right-4">
          Recomendado
        </Sticker>
      )}
      <span className="font-mono text-xs font-bold uppercase">Suscripción</span>
      <h3 className={cn(titleClass.featured, "mt-2")}>{title}</h3>
      <p className="mt-2">{detail}</p>
      <p className="text-ink-soft mt-4 text-xs">
        El precio final y los medios de pago se muestran antes de confirmar.
      </p>
      <button
        type="button"
        onClick={onSelect}
        className={`${highlighted ? btnPrimary : btnSecondary} mt-5 w-full justify-between px-4 py-3`}
      >
        Elegir plan {title.toLowerCase()} <span aria-hidden="true">↗</span>
      </button>
    </article>
  );
}

// Subscriptions aren't on sale yet: the "Suscripción" card and its index link
// are hidden, not removed. Set to true to bring both back.
const SHOW_SUBSCRIPTION = false;

// Payments go through Stripe, which collects the billing details at checkout,
// so the "Datos de facturación" card and its index link are hidden, not
// removed. Set to true to bring both back.
const SHOW_BILLING = false;

/** Page header and section stickers, shared by the page and its loading state. */
function SettingsTop() {
  return (
    <>
      <PageHeader
        kicker="Tu cuenta"
        title="Configuración"
        description={
          SHOW_BILLING && SHOW_SUBSCRIPTION
            ? "Tu perfil, facturación, suscripción y comprobantes."
            : SHOW_BILLING
              ? "Tu perfil, facturación y comprobantes."
              : SHOW_SUBSCRIPTION
                ? "Tu perfil, suscripción y comprobantes."
                : "Tu perfil y comprobantes."
        }
        doodle="key-lavender"
        tone="grape"
      />
      <nav
        aria-label="Secciones de tu cuenta"
        className="mt-7 flex flex-wrap gap-3"
      >
        {settingsSections
          .filter(
            (section) =>
              (SHOW_SUBSCRIPTION || section.id !== "suscripcion") &&
              (SHOW_BILLING || section.id !== "facturacion"),
          )
          .map((section, index) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className="inline-block"
            >
              <Sticker
                tone={section.tone}
                tilt={index % 2 ? 1.5 : -1.5}
                className="normal-case"
              >
                {section.label}
              </Sticker>
            </a>
          ))}
      </nav>
    </>
  );
}

/** A field while loading: a label-sized bar over an input-sized block. */
function GhostField({
  labelWidth = "w-24",
  tall,
  className,
}: {
  labelWidth?: string;
  tall?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <LoadingBlock className={`h-4 ${labelWidth}`} />
      <LoadingBlock
        className={cn(
          "rounded-control mt-2 w-full",
          tall ? "h-[4.5rem]" : "h-12",
        )}
      />
    </div>
  );
}

/** A card heading while loading: the tilted tile, kicker, title and line. */
function GhostHeading({ titleWidth }: { titleWidth: string }) {
  return (
    <div className="flex items-start gap-4">
      <LoadingBlock className="h-12 w-12 shrink-0 rotate-[-4deg]" />
      <div className="min-w-0 flex-1">
        <LoadingBlock className="mt-1 h-3.5 w-20" />
        <LoadingBlock className={`mt-2 h-10 ${titleWidth} max-w-full`} />
        <LoadingBlock className="mt-2.5 h-4 w-56 max-w-full" />
      </div>
    </div>
  );
}

/**
 * Configuración while the account loads: the page's own cards (same borders,
 * shadows, grid and sizes) with every title, label and field as a
 * shimmering block, so it reads clearly as loading and lands exactly where
 * the content will.
 */
export function SettingsLoading() {
  return (
    <div role="status" aria-label="Cargando tu cuenta">
      <span className="sr-only">Cargando…</span>
      <div className="relative pr-0 sm:pr-28">
        <LoadingBlock className="h-7 w-28 rotate-[-2deg]" />
        <LoadingBlock className="mt-4 h-12 w-80 max-w-full" />
        <LoadingBlock className="mt-3 h-4 w-52 max-w-full" />
        <LoadingBlock className="absolute -top-2 right-0 hidden h-20 w-20 rounded-[1.25rem] sm:block" />
      </div>
      <div className="mt-7 flex flex-wrap gap-3">
        <LoadingBlock className="h-8 w-20" />
        <LoadingBlock className="h-8 w-32" />
      </div>
      <div className="border-ink bg-bone shadow-brutal border-surface mt-8 p-6">
        <GhostHeading titleWidth="w-64" />
        <div className="mt-5 grid gap-5 md:grid-cols-[9rem_minmax(0,1fr)]">
          <div className="flex items-center gap-4 md:flex-col md:items-start md:gap-2">
            <LoadingBlock className="hidden h-3.5 w-28 md:block" />
            <LoadingBlock className="h-24 w-24" />
            <LoadingBlock className="h-8 w-32 md:mt-2" />
          </div>
          <div className="grid content-start gap-4 sm:grid-cols-2">
            <GhostField labelWidth="w-32" />
            <GhostField labelWidth="w-36" />
            <GhostField labelWidth="w-20" tall className="sm:col-span-2" />
          </div>
        </div>
        <div className="border-ink/20 mt-5 grid gap-4 border-t border-dashed pt-5 sm:grid-cols-2 lg:grid-cols-3">
          {["w-12", "w-36", "w-16", "w-20", "w-28", "w-32"].map(
            (width, index) => (
              <GhostField key={index} labelWidth={width} />
            ),
          )}
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LoadingBlock className="h-5 w-5" />
            <div>
              <LoadingBlock className="h-3.5 w-28" />
              <LoadingBlock className="mt-1.5 h-3 w-64 max-w-full" />
            </div>
          </div>
          <LoadingBlock className="rounded-control h-12 w-40" />
        </div>
      </div>
      <div className="border-ink bg-bone shadow-brutal border-surface mt-7 p-6">
        <GhostHeading titleWidth="w-80" />
        <div className="border-ink/30 border-chip mt-6 flex flex-col items-center border-dashed p-8">
          <LoadingBlock className="h-14 w-12" />
          <LoadingBlock className="mt-4 h-7 w-56 max-w-full" />
          <LoadingBlock className="mt-3 h-5 w-80 max-w-full" />
        </div>
      </div>
    </div>
  );
}

// Section index under the page header (anchors to each card below).
const settingsSections = [
  { id: "perfil", label: "Perfil", tone: "acid" },
  { id: "facturacion", label: "Facturación", tone: "lemon" },
  { id: "suscripcion", label: "Suscripción", tone: "grape" },
  { id: "comprobantes", label: "Comprobantes", tone: "bubble" },
] as const;

// Section title on the landing's language: tilted icon tile, kicker with the
// pink square, display title and a one-line description.
function SettingsHeading({
  icon: Icon,
  tone,
  kicker,
  title,
  description,
}: {
  icon: typeof UserRound;
  tone: string;
  kicker: string;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-4">
      <span
        aria-hidden="true"
        className={`border-ink border-chip shadow-brutal grid h-12 w-12 shrink-0 rotate-[-4deg] place-items-center ${tone}`}
      >
        <Icon size={22} />
      </span>
      <div className="min-w-0">
        <p className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.06em]">
          <span
            aria-hidden="true"
            className="border-ink bg-bubble border-chip inline-block size-2.5 rotate-[-15deg]"
          />
          {kicker}
        </p>
        <h2 className={cn(titleClass.section, "mt-1")}>{title}</h2>
        <p className="text-ink-soft mt-1 text-sm">{description}</p>
      </div>
    </div>
  );
}

// Receipt status as a small chip; the word carries the meaning, the colour
// only reinforces it.
function InvoiceStatus({ status }: { status: string }) {
  const look = {
    paid: { label: "Pagada", tone: "bg-acid" },
    open: { label: "Pendiente", tone: "bg-lemon" },
    draft: { label: "Borrador", tone: "bg-paper" },
    void: { label: "Anulada", tone: "bg-paper-dim" },
  }[status] ?? { label: status, tone: "bg-paper" };
  return (
    <span
      className={`border-ink border-chip inline-block px-2 py-0.5 font-mono text-xs font-bold uppercase ${look.tone}`}
    >
      {look.label}
    </span>
  );
}
